import {
  Button,
  Col,
  Descriptions,
  List,
  Row,
  Skeleton,
  Typography,
} from "antd";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { DescriptionsItemType } from "antd/es/descriptions";
import dayjs from "dayjs";
import { PrinterOutlined } from "@ant-design/icons";
import InvoiceStatusComponent from "components/common/InvoiceStatus";
import { IGetInvoiceDetailsResponse } from "api/requests";
import PrintInvoiceLabel from "./PrintInvoiceLabel";

export interface IInvoiceDetailsSegmentProps {
  data: IGetInvoiceDetailsResponse | undefined;
  loading: boolean;
}

function InvoiceDetailsSegment({ data, loading }: IInvoiceDetailsSegmentProps) {
  const nav = useNavigate();
  const [printLabelOpen, setPrintLabelOpen] = useState(false);

  const company = useSelector((state: any) => state.company);

  const descriptionItems: DescriptionsItemType[] = [
    {
      key: "invoiceNumber",
      label: "Number",
      children: data?.invoiceNumber,
    },
    {
      key: "customerName",
      label: "Customer Name",
      children: data?.customer?.customerName,
    },
    {
      key: "customerPhone",
      label: "Customer Phone",
      children: data?.customer?.customerPhone,
    },
    {
      key: "invoiceDate",
      label: "Date",
      children: dayjs(data?.invoiceDate).format("DD/MM/YYYY"),
    },
    {
      key: "invoiceDueDate",
      label: "Due Date",
      children: dayjs(data?.invoiceDueDate).format("DD/MM/YYYY"),
    },
    {
      key: "invoiceStatus",
      label: "Status",
      children: <InvoiceStatusComponent status={data?.invoiceStatus as any} />,
    },
    {
      key: "location",
      label: "Location",
      children: (data?.location as any)?.locationName ?? "-",
    },
    {
      key: "paymentMethod",
      label: "Payment Method",
      children: data?.paymentMethod?.paymentMethodName || "-",
    },
    {
      key: "taxType",
      label: "Tax Type",
      children: data?.tax?.taxType ?? "-",
    },
    {
      key: "taxRate",
      label: "Tax Rate",
      children: data?.tax?.taxRate ?? "-",
    },
    {
      key: "taxTotal",
      label: "Tax Total",
      children: data?.taxAmount
        ? `${data?.taxAmount} ${company.currencySymbol}`
        : "-",
    },
    {
      key: "discountScheme",
      label: "Discount Scheme",
      children: data?.discountScheme ?? "-",
    },
    {
      key: "discountAmount",
      label: "Discount Amount",
      children: data?.discountAmount ?? "-",
    },
    {
      key: "invoiceDiscountTotal",
      label: "Discount Total",
      children: data?.invoiceDiscountTotal
        ? `${data?.invoiceDiscountTotal} ${company.currencySymbol}`
        : "-",
    },
    {
      key: "totalPaidAmount",
      label: "Paid Amount",
      children:
        data?.totalPaidAmount && data?.totalPaidAmount > 0
          ? `${data?.totalPaidAmount} ${company.currencySymbol}`
          : "-",
    },
    {
      key: "invoiceTotal",
      label: "Total",
      children: `${data?.invoiceTotalAmount} ${company.currencySymbol}`,
    },
    {
      key: "remainingAmount",
      label: "Remaining Amount",
      children: data?.totalPaidAmount
        ? `${data?.invoiceTotalAmount - data?.totalPaidAmount} ${
            company.currencySymbol
          }`
        : "-",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <Row>
        <Col xs={24}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <Typography.Title level={4}>Invoice Details</Typography.Title>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={() => setPrintLabelOpen(true)}
              disabled={loading || !data}
            >
              Print Label
            </Button>
          </div>
          <Skeleton loading={!!loading} active>
            <Descriptions
              style={{ width: "100%", margin: "2rem 0" }}
              layout="vertical"
              items={descriptionItems}
            />
          </Skeleton>
        </Col>

        <Col xs={24} style={{ margin: "1rem 0" }}>
          <List
            loading={!!loading}
            renderItem={(item: any, index) => (
              <List.Item
                actions={[
                  <Button
                    type="link"
                    onClick={() =>
                      nav(`/inventory/products/${item.product?.idProduct}`)
                    }
                  >
                    View
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Typography.Text strong style={{ fontSize: 16 }}>
                      {[
                        item.product?.brand?.brandName,
                        item.product?.productName,
                        item.product?.productCategory?.productCategoryName,
                        item.product?.productSubcategory?.productCategoryName,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    </Typography.Text>
                  }
                  description={
                    <div style={{ marginTop: 4 }}>
                      <Typography.Text style={{ fontSize: 15, color: "#1890ff" }}>
                        <span style={{ fontWeight: 500 }}>{item.quantity}</span> x{" "}
                        <span style={{ fontWeight: 500 }}>{item.unitPrice}</span>{" "}
                        <span style={{ fontWeight: 600 }}>{company.currencySymbol}</span>
                      </Typography.Text>
                      {(data as any)?.inventoryTransactions?.filter(
                        (ii: any) =>
                          ii.product === item?.product?.idProduct &&
                          ii?.transactionType === "TRANSFER" &&
                          ii.quantity > 0
                      )?.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Typography.Text style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>
                            Transfers:
                          </Typography.Text>
                          <ul style={{ margin: "4px 0 0 0", paddingLeft: 18, color: "#888" }}>
                            {(data as any)?.inventoryTransactions
                              ?.filter(
                                (ii: any) =>
                                  ii.product === item?.product?.idProduct &&
                                  ii?.transactionType === "TRANSFER" &&
                                  ii.quantity > 0
                              )
                              ?.map((ii: any, idx: number) => (
                                <li key={idx} style={{ fontSize: 13, marginBottom: 2 }}>
                                  <span style={{ color: "#b37feb", fontWeight: 500 }}>{ii.locationFrom?.locationName}</span>
                                  <span style={{ color: "#aaa", margin: "0 4px" }}>→</span>
                                  <span style={{ color: "#52c41a", fontWeight: 500 }}>{ii.locationTo?.locationName}</span>
                                  <span style={{ color: "#1890ff", marginLeft: 8, fontWeight: 600 }}>({ii.quantity})</span>
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  }
                  key={index}
                />
                <List.Item.Meta
                  title={""}
                  description={
                    <Typography.Text>
                      {item.totalPrice} {company.currencySymbol}
                    </Typography.Text>
                  }
                />
              </List.Item>
            )}
            dataSource={data?.invoiceItems}
          />
        </Col>
      </Row>

      <PrintInvoiceLabel
        open={printLabelOpen}
        onClose={() => setPrintLabelOpen(false)}
        invoiceData={data}
      />
    </motion.div>
  );
}

export default InvoiceDetailsSegment;
